/**
 * Waits for a specified count of elements to be present in a container using MutationObserver.
 * @param containerQuery Selector (class|id|tag) for the parent container (or empty to use `document`)
 * @param elementQuery   Selector (class|id|tag) for the elements we want to find
 * @param elementMinCount Minimal number of elements needed
 * @param timeOutMS      Timeout in milliseconds
 * @returns A Promise resolving with the NodeList of matching elements, or rejecting on timeout
 */
export function waitForElementsToRenderWithMutation(
  containerQuery: string,
  elementQuery: string,
  elementMinCount: number,
  timeOutMS: number
): Promise<NodeListOf<HTMLElement>> {
  return new Promise((resolve, reject) => {
    const containerElement = containerQuery
      ? document.querySelector(containerQuery)
      : document;

    if (!containerElement) {
      reject(new Error(`Container not found: ${containerQuery}`));
      return;
    }

    const startTime = performance.now();

    // Checks if we have enough elements
    const checkElements = () => {
      const foundElements =
        containerElement.querySelectorAll<HTMLElement>(elementQuery);
      if (foundElements.length >= elementMinCount) {
        // We have enough; stop observing and resolve
        observer.disconnect();
        resolve(foundElements);
      } else {
        // Check if we are out of time
        if (performance.now() - startTime > timeOutMS) {
          observer.disconnect();
          reject(
            new Error(
              `Timeout: Did not find at least ${elementMinCount} elements matching ${elementQuery} within ${timeOutMS} ms`
            )
          );
        }
      }
    };

    // Create a MutationObserver to watch for new elements being added
    const observer = new MutationObserver(() => {
      checkElements();
    });

    // Observe child/subtree modifications
    observer.observe(containerElement, {
      childList: true,
      subtree: true,
    });

    // Do an immediate check (in case elements are already present)
    checkElements();
  });
}
